import { Add, CancelOutlined, Search } from "@material-ui/icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useAsyncFn } from "react-use";
import styled from "styled-components";
import { APISearchParams } from "../../api/base";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import { useStore } from "../../store";
import { Page } from "../Page";
import NewProject from "./NewProject";
import Projects from "./Projects";

const RootContainer = styled.div`
  margin: 1.875rem 3.75rem;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid #b9b9b9;
  border-radius: 0.25rem;
  overflow: hidden;
  height: 38px;
  width: fit-content;

  &:focus-within {
    border-color: #4caf50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const SearchInput = styled.input`
  padding: 0.4rem 0.75rem;
  border: none;
  font-size: 0.875rem;
  outline: none;
  min-width: 280px;
  background: #ffffff;
`;

const SearchActionButton = styled.button`
  background: transparent;
  border: none;
  border-left: 1px solid #b9b9b9;
  color: #666;
  padding: 0 0.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const SearchSubmitButton = styled(SearchActionButton)`
  background: #4caf50;
  border-left: 1px solid #43a047;
  color: #ffffff;

  &:hover {
    background: #43a047;
    color: #ffffff;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const ProjectsDrive: React.FC = () => {
  const store = useStore();
  const urlPrefix = `/projects/`;
  const [newProjectModalShow, setNewProjectModalShow] =
    useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") setSearchQuery(inputValue);
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchQuery("");
  };

  const fetchProjectFoldersList = async (params: APISearchParams) => {
    return store.projectFolders.listActive(1, -1, params);
  };

  const [
    { loading: projectFoldersLoading, value: projectFolders },
    fetchProjectFolders,
  ] = useAsyncFn(async () => {
    const params: APISearchParams = {
      is_active: true,
    };

    await fetchProjectFoldersList(params);
    return Array.from(store.projectFolders.listActiveItems.values());
  });

  const fetchProjectsList = async (params: APISearchParams) => {
    return store.projects.listActive(1, -1, params);
  };

  const [{ loading: projectsLoading, value: activeProject }, fetchProjects] =
    useAsyncFn(async () => {
      const params: APISearchParams = {
        is_active: true,
      };

      const { count } = await fetchProjectsList(params);
      return {
        count,
        results: Array.from(store.projects.listActiveItems.values()),
      };
    });

  const fetchArchivedProjectFoldersList = async (params: APISearchParams) => {
    return store.projectFolders.listArchived(1, -1, params);
  };

  const [
    { loading: archivedProjectFoldersLoading, value: archivedProjectFolders },
    fetchArchivedProjectFolders,
  ] = useAsyncFn(async () => {
    const params: APISearchParams = {
      is_active: false,
    };
    await fetchArchivedProjectFoldersList(params);
    return Array.from(store.projectFolders.listArchivedItems.values());
  });

  const fetchArchivedProjectsList = async (params: APISearchParams) => {
    return store.projects.listArchived(1, -1, params);
  };

  const [
    { loading: archivedProjectsLoading, value: archivedProjects },
    fetchArchivedProjects,
  ] = useAsyncFn(async () => {
    const params: APISearchParams = {
      is_active: false,
    };

    const { count } = await fetchArchivedProjectsList(params);
    return {
      count,
      results: Array.from(store.projects.listArchiveItems.values()),
    };
  });

  useEffect(() => {
    fetchProjectFolders();
    fetchProjects();
    fetchArchivedProjectFolders();
    fetchArchivedProjects();
  }, [
    fetchProjectFolders,
    fetchProjects,
    fetchArchivedProjectFolders,
    fetchArchivedProjects,
  ]);

  const handleFolderSubmit = async () => {
    await Promise.all([fetchProjectFolders(), fetchProjects()]);
  };

  const handleArchivedFolderSubmit = async () => {
    await Promise.all([fetchArchivedProjectFolders(), fetchArchivedProjects()]);
  };

  const handleProjectSubmit = async () => {
    await fetchProjects();
  };

  const handleArchivedProjectSubmit = async () => {
    await fetchArchivedProjects();
  };

  const handleArchiveRestore = async () => {
    await Promise.all([fetchProjects(), fetchArchivedProjects()]);
  };

  return (
    <Page
      title="Projects"
      breadcrumb={[
        {
          title: "Projects Drive",
          subtitle: "Projects",
          link: "/projects",
        },
      ]}
    >
      <RootContainer>
        <TopBar>
          <Button
            variant="green"
            size="page"
            onClick={() => setNewProjectModalShow(true)}
          >
            <Add />
            New Project
          </Button>
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search all projects by name..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            {inputValue && (
              <SearchActionButton type="button" onClick={handleClearSearch}>
                <CancelOutlined style={{ fontSize: "1rem" }} />
              </SearchActionButton>
            )}
            <SearchSubmitButton type="button" onClick={() => setSearchQuery(inputValue)}>
              <Search style={{ fontSize: "1rem" }} />
            </SearchSubmitButton>
          </SearchWrapper>
        </TopBar>

        {projectsLoading &&
        projectFoldersLoading &&
        archivedProjectFoldersLoading &&
        archivedProjectsLoading ? (
          <Loading />
        ) : (
          <>
            <Projects
              title="Active Projects"
              urlPrefix={urlPrefix}
              projects={activeProject?.results}
              folders={projectFolders}
              projectCount={activeProject?.count}
              searchQuery={searchQuery}
              onFolderSubmit={handleFolderSubmit}
              onProjectSubmit={handleProjectSubmit}
              onArchiveRestore={handleArchiveRestore}
            />
            <Projects
              archived
              title="Archived Projects"
              urlPrefix={urlPrefix}
              projects={archivedProjects?.results}
              projectCount={archivedProjects?.count}
              folders={archivedProjectFolders}
              searchQuery={searchQuery}
              onFolderSubmit={handleArchivedFolderSubmit}
              onProjectSubmit={handleArchivedProjectSubmit}
              onArchiveRestore={handleArchiveRestore}
              noFilter
            />
          </>
        )}

        <NewProject
          isOpen={newProjectModalShow}
          setModal={setNewProjectModalShow}
        />
      </RootContainer>
    </Page>
  );
};

export default observer(ProjectsDrive);
